import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import config from '../config.json';
import { detectUserLanguageByIP } from '../utils/ipLanguageMapper';

const SettingsContext = createContext();

export function SettingsProvider({ children }) {
  const { i18n } = useTranslation();

  const defaultSettings = {
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '12hr',
    theme: 'Light',
    colorTheme: '#20A8E0',
    mapview: 'Roadmap',
    fontStyle: 'Roboto',
    mapLayers: 'Traffic',
    defaultLanguage: 'en',
    defaultApplication: 'vehicleTrack',
  };

  const [settings, setSettings] = useState(() => {
    // Load settings from localStorage on mount to handle refresh
    const savedSettings = localStorage.getItem('appSettings');
    return savedSettings ? { ...defaultSettings, ...JSON.parse(savedSettings) } : defaultSettings;
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(0);

  const fetchSettings = useCallback(
    async (organizationId) => {
      const manualUserLang = localStorage.getItem('user_manual_language') || localStorage.getItem('preferredLanguage');
      const sessionDetectedLang = sessionStorage.getItem('session_detected_lang');

      if (!organizationId) {
        console.warn('No organizationId provided, using default/local settings');
        const detected = sessionDetectedLang || await detectUserLanguageByIP();
        if (detected) {
          sessionStorage.setItem('session_detected_lang', detected);
          if (i18n.setDetectedFallback) i18n.setDetectedFallback(detected);
        }

        let initialLang = manualUserLang || detected || 'en';

        const initialSettings = { ...defaultSettings, defaultLanguage: initialLang };
        setSettings((prev) => ({ ...initialSettings, ...prev }));
        i18n.changeLanguage(initialLang);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(
          `${config.baseURL}/appSettings/organization?organizationId=${organizationId}`,
          { withCredentials: true }
        );

        const orgDefaultLanguage = response?.data?.defaultLanguage?.toLowerCase();

        let resolvedLanguage = manualUserLang || orgDefaultLanguage;

        if (!resolvedLanguage || resolvedLanguage === 'ud') {
          const detectedLang = sessionDetectedLang || await detectUserLanguageByIP();
          if (detectedLang) {
            sessionStorage.setItem('session_detected_lang', detectedLang);
            if (i18n.setDetectedFallback) i18n.setDetectedFallback(detectedLang);
          }
          if (!resolvedLanguage) {
            resolvedLanguage = detectedLang;
          }
        }

        const fetchedSettings = {
          ...defaultSettings,
          ...response.data,
          defaultLanguage: resolvedLanguage,
        };

        setSettings(fetchedSettings);
        localStorage.setItem('appSettings', JSON.stringify(fetchedSettings));
        i18n.changeLanguage(fetchedSettings.defaultLanguage);
      } catch (error) {
        console.error('Error fetching settings:', error);
        let localLanguage = manualUserLang || sessionDetectedLang;
        if (!localLanguage) {
          localLanguage = await detectUserLanguageByIP();
          if (localLanguage) sessionStorage.setItem('session_detected_lang', localLanguage);
        }
        const fallbackSettings = {
          ...defaultSettings,
          defaultLanguage: localLanguage,
        };
        setSettings(fallbackSettings);
        localStorage.setItem('appSettings', JSON.stringify(fallbackSettings));
        i18n.changeLanguage(localLanguage);
      } finally {
        setLoading(false);
      }
    },
    [i18n]
  );

  const updateSettings = (newSettings) => {
    const updatedSettings = {
      ...settings,
      ...newSettings,
      defaultLanguage:
        newSettings.defaultLanguage?.toLowerCase() || settings.defaultLanguage,
    };
    console.log('Updating settings:', updatedSettings);
    setSettings(updatedSettings);
    setLastUpdate(Date.now());
    localStorage.setItem('appSettings', JSON.stringify(updatedSettings)); // Persist to localStorage
    i18n.changeLanguage(updatedSettings.defaultLanguage);
    localStorage.setItem('i18nextLng', updatedSettings.defaultLanguage);
    localStorage.setItem('user_manual_language', updatedSettings.defaultLanguage);
    localStorage.setItem('preferredLanguage', updatedSettings.defaultLanguage);
    if (updatedSettings.defaultLanguage === 'ud') {
      const detected = sessionStorage.getItem('session_detected_lang');
      if (detected) {
        if (i18n.setDetectedFallback) i18n.setDetectedFallback(detected);
      } else {
        detectUserLanguageByIP().then((ipLang) => {
          if (ipLang) {
            sessionStorage.setItem('session_detected_lang', ipLang);
            if (i18n.setDetectedFallback) i18n.setDetectedFallback(ipLang);
          }
        });
      }
    }
  };

  const persistLanguageSetting = useCallback(
    async (languageCode, organizationId) => {
      const normalizedLanguage =
        String(languageCode || '').trim().toLowerCase() ||
        settings.defaultLanguage ||
        defaultSettings.defaultLanguage;
      const resolvedOrganizationId = Number(
        organizationId ||
        settings.organizationId ||
        localStorage.getItem('organizationId')
      );

      const optimisticSettings = {
        ...settings,
        organizationId: resolvedOrganizationId || settings.organizationId,
        defaultLanguage: normalizedLanguage,
      };

      updateSettings(optimisticSettings);

      if (!resolvedOrganizationId) {
        return optimisticSettings;
      }

      const payload = {
        ...optimisticSettings,
        organizationId: resolvedOrganizationId,
      };

      delete payload.createdAt;
      delete payload.updatedAt;

      const response = await axios({
        method: payload.id ? 'put' : 'post',
        url: `${config.baseURL}/appSettings`,
        data: payload,
        withCredentials: true,
      });

      const savedSettings = {
        ...optimisticSettings,
        ...response.data,
        defaultLanguage:
          response.data?.defaultLanguage?.toLowerCase() || normalizedLanguage,
      };

      updateSettings(savedSettings);
      return savedSettings;
    },
    [settings, updateSettings]
  );

  const applyTheme = (theme, colorTheme, fontStyle, language) => {
    document.documentElement.setAttribute('data-theme', theme.toLowerCase());
    document.documentElement.style.setProperty('--primary-color', colorTheme);
    document.documentElement.style.setProperty('--font-family', fontStyle);
    document.documentElement.setAttribute('dir', language === 'ar' ? 'rtl' : 'ltr');
  };

  useEffect(() => {
    if (settings && !loading && settings.defaultLanguage) {
      // console.log('Applying settings:', settings);
      applyTheme(
        settings.theme,
        settings.colorTheme,
        settings.fontStyle,
        settings.defaultLanguage
      );
    }
  }, [settings, loading]);

  // Fetch settings on mount (runs IP detection if no organizationId or saved language)
  useEffect(() => {
    const organizationId = localStorage.getItem('organizationId');
    fetchSettings(organizationId);
  }, [fetchSettings]);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        loading,
        updateSettings,
        fetchSettings,
        persistLanguageSetting,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}
